import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux'
import { useMatch } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronRight, faChevronLeft } from '@fortawesome/free-solid-svg-icons'

import './MainSideBar.css'

import { useSelector } from 'react-redux'
import { selectActivePanel, togglePanel } from '../app/interface.js'

import FiltersAndLayers from './FiltersAndLayers.jsx'
import Annotate from './Annotate.jsx'
import {selectCanDo, selectCanSeeHelpButtons} from "../app/auth.js"
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { selectProjectId } from '../app/projects';


function MainSideBar(props) {
  const [closed, setClosed] = useState(false)
  const dispatch = useDispatch()
  const activePanel = useSelector(selectActivePanel)
  const canDo = useSelector(selectCanDo)
  const projectId = useSelector(selectProjectId);
  const isPhotoViewer = Boolean(useMatch('/photo-viewer'));
  const canSeeHelpButtons = useSelector(selectCanSeeHelpButtons);

  // Check permissions (only if projectId exists) - Memoized to avoid recalculation
  const canCreateAnnotation = useMemo(() => {
    return projectId ? canDo("createAnnotation", {projectId}) : false;
  }, [projectId, canDo]);

  // Ensure activePanel is valid based on permissions - only redirect if trying to access forbidden annotate
  // In photo viewer, analyse tab is hidden so don't redirect there
  useEffect(() => {
    if (isPhotoViewer) return;
    if (projectId && activePanel === "annotate" && !canCreateAnnotation) {
      dispatch(togglePanel({id: "analyse"}));
    }
  }, [canCreateAnnotation, projectId, dispatch, isPhotoViewer]);

  if (!projectId) {
    return null;
  }

  // Determine which content to show based on activePanel and permissions
  const renderContent = () => {
    if (activePanel === "annotate" && canCreateAnnotation) {
      return <Annotate/>;
    }
    // Photo viewer: only annotate; Ortho: default to analyse for readers
    if (isPhotoViewer) {
      return canCreateAnnotation ? <Annotate/> : null;
    }
    return <FiltersAndLayers/>;
  };

  return (
    <aside className={`sidebar sidebar-left ${closed ? "closed" : ""}`}>
      <div className="tab-menu">
        {!isPhotoViewer && (
        <div 
          className={activePanel === "analyse" ? "active" : ""} 
          onClick={() => dispatch(togglePanel({id: "analyse"}))}
        >
          Analyse
        </div>
        )}
        {canCreateAnnotation && (
          <div 
            className={activePanel === "annotate" ? "active" : ""} 
            onClick={() => dispatch(togglePanel({id: "annotate"}))}
          >
            Annotate
            {canSeeHelpButtons && (
              <a
                href="https://learn.everenn-app.fr/index.php/Manuel_d%27utilisateur#Gestion_des_annotations"
                target="_blank"
                rel="noopener noreferrer"
                title="Help on annotations management"
                onClick={(e) => e.stopPropagation()}
                style={{ marginLeft: 4, display: 'inline-flex', color: 'inherit' }}
              >
                <HelpOutlineIcon sx={{ fontSize: 16 }} />
              </a>
            )}
          </div>
        )}
        <div>
          <span className="sidebar-closer" onClick={(e) => setClosed(!closed)}>
            {closed ? <FontAwesomeIcon icon={faChevronRight}/> : <FontAwesomeIcon icon={faChevronLeft}/>}
          </span>
        </div>
      </div>
      <div className="tab-content">
        {renderContent()}
      </div>
    </aside>
  );
}

export default MainSideBar;